import React, { Key } from 'react';
import jwt_decode from 'jwt-decode';
import { arrayMove } from '@dnd-kit/sortable';
import { IAurReq, IRegulatoryReq } from 'store/ducks/ClientReq/types';
import { Entity, Scope } from 'store/ducks/InternalControls/types';
import type { TransferItem } from 'antd/es/transfer';
import dayjs from 'dayjs';
const ORG_CONTEXT_KEY = 'orgContext';

const Helpers = {
  isObjEmpty: (obj: any) => Object.keys(obj).length === 0,
  formatDate: (date: any) => {
    const dt = new Date(date);
    return dt.toLocaleString('de', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  getToken: () => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return user?.token;
  },

  getUserInfo: () => {
    const token = Helpers.getToken();
    return token && jwt_decode(token);
  },

  getFilterParams: (activeFilters: Record<string, string[]>) => {
    return activeFilters;
  },

  convertToSelectOptions: (data: Array<any>) =>
    data && data.length > 0
      ? data.map((item: string) => ({ value: item, label: item }))
      : [],
  onDragEnd: ({ active, over }: any, setState: any, filteredKey: string) => {
    if (active.id !== over?.id) {
      setState((prev: any) => {
        const activeIndex = prev.findIndex(
          (i: any) => i[filteredKey] === active.id
        );

        const overIndex = prev.findIndex(
          (i: any) => i[filteredKey] === over?.id
        );

        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  },

  filterRegReqs: (
    regReqs: IRegulatoryReq[],
    checkedAurReqs: number[],
    checked: boolean
  ) => {
    const filteredRegReqs: IRegulatoryReq[] = regReqs.reduce(
      (result: IRegulatoryReq[], obj: IRegulatoryReq) => {
        const filteredAurReqs: { [key: string]: IAurReq } = {};

        for (const key in obj.AUR_REQ) {
          const aurReq = obj.AUR_REQ[key as keyof typeof obj.AUR_REQ];

          if (
            (checked && checkedAurReqs.includes(aurReq.id)) ||
            (!checked && !checkedAurReqs.includes(aurReq.id))
          ) {
            filteredAurReqs[key] = aurReq;
          }
        }

        if (Object.keys(filteredAurReqs).length > 0) {
          result.push({
            ...obj,
            // @ts-ignore
            AUR_REQ: filteredAurReqs
          });
        }

        return result;
      },
      []
    );

    return filteredRegReqs;
  },
  removeAtIndex: (array: any[], index: number) => [
    ...array.slice(0, index),
    ...array.slice(index + 1)
  ],
  insertAtIndex: (array: any[], index: number, item: unknown) => [
    ...array.slice(0, index),
    item,
    ...array.slice(index)
  ],
  toTransferItems: (items: Entity[], prefix: string): TransferItem[] =>
    items.map((i) => ({
      key: `${prefix}-${i.id}`,
      title: i.name
    })),

  areKeysEqual: (a: Key[] = [], b: Key[] = []) => {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  },

  normalizeDate: (value?: string | null): string | null => {
    if (!value) return null;

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  },
  buildScopeFromFilters: (query: Record<string, any>): Scope => {
    const toNumberOrNull = (value: any): number | null => {
      const n = Number(value);
      return isNaN(n) ? null : n;
    };

    return {
      group_id: toNumberOrNull(
        Array.isArray(query.group) ? query.group[0] : query.group
      ),
      division_id: toNumberOrNull(
        Array.isArray(query.division) ? query.division[0] : query.division
      ),
      region_id: toNumberOrNull(
        Array.isArray(query.region) ? query.region[0] : query.region
      ),
      legal_entity_id: toNumberOrNull(
        Array.isArray(query.legal_entity)
          ? query.legal_entity[0]
          : query.legal_entity
      ),
      business_unit_id: toNumberOrNull(
        Array.isArray(query.business_unit)
          ? query.business_unit[0]
          : query.business_unit
      )
    };
  },

  emptyFilledOnFilter:
    <T extends Record<string, any>>(field: keyof T) =>
    (filterValue: string | number | boolean, record: T) => {
      // Always show rows that the user has started editing (pinned until page refresh)
      if (record.isNew) return true;
      if (record._pinned) return true;

      const value = record[field];

      const isFilled =
        value !== null &&
        value !== undefined &&
        (typeof value === 'string'
          ? value.trim() !== ''
          : typeof value === 'object'
          ? (value as any).id != null
          : false);

      return filterValue === 'filled' ? isFilled : !isFilled;
    },

  loadOrgContext: () => {
    try {
      const stored = localStorage.getItem(ORG_CONTEXT_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  },

  saveOrgContext: (data: any) => {
    try {
      localStorage.setItem(ORG_CONTEXT_KEY, JSON.stringify(data));
    } catch {
      // ignore storage errors
    }
  },

  toApiFilters: (query: any) => {
    const extractLabel = (v: any) => {
      if (!v) return [];

      const arr = Array.isArray(v) ? v : [v];

      return arr.map((item) => {
        if (typeof item === 'object') {
          return item.label ?? item.value;
        }
        return item;
      });
    };

    const extractId = (v: any) => {
      if (!v) return [];

      const arr = Array.isArray(v) ? v : [v];
      return arr.map((item) => (typeof item === 'object' ? item.value : item));
    };

    return {
      group_id: extractId(query.group || query.group_id),
      division_id: extractId(query.division),
      region_id: extractId(query.region),
      legal_entity_id: extractId(query.legalEntity),
      business_unit_id: extractId(query.businessUnit),
      main_organization_id: query.mainOrganizationId || null,

      industry: extractLabel(query.industryType),
      module: extractLabel(query.module),

      industryId: extractId(query.industryType),
      moduleId: extractId(query.module)
    };
  },

  safeId: (v: any) => (typeof v === 'object' ? v.value ?? v.id : v),

  normalizeFilters: (filters: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(filters)
        .map(([key, value]) => [key, Array.isArray(value) ? value[0] : value])
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== ''
        )
    );
  }
};

export default Helpers;
